import { useMemo } from 'react';
import { OptimusProvider } from '@mews-commander/ui';
import { lt } from '@mews/framework';
import { WithAccountId } from '@mews-commander/types';
import { HistoricalPaymentTableRecord, PaymentTableRecordPaymentStatus } from '@mews-commander/data-access';
import { TableHeader, StandaloneTable, useTableState, Stack } from '@optimus-web/core';
import { useGetPaymentPlanDetail } from '../PaymentPlans/queries';
import { comparePaymentPlanDetailOverviewRecordsDesc, showNextScheduledPayment } from '../PaymentPlans/utils';
import { WithPaymentPlanId } from '../PaymentPlans/types';
import { PaymentPlanDetailsOverviewTableHeaderActions } from './PaymentPlanDetailsOverviewTableHeaderActions';
import {
    getPaymentPlanDetailsOverviewTableMapping,
    PaymentPlanDetailOverviewTableRecord,
} from './paymentPlanDetailsOverviewTableMapping';

export const PaymentPlanDetailsOverviewTable = ({ paymentPlanId, accountId }: WithPaymentPlanId & WithAccountId) => {
    const [tableState, setTableState] = useTableState<PaymentPlanDetailOverviewTableRecord>({
        pagination: {
            show: false,
        },
    });
    const { data, isFetching } = useGetPaymentPlanDetail({
        id: paymentPlanId,
    });

    // Todo: FINTECH-7246
    const cancelledPayments =
        data?.canceledPaymentsWithAmounts.map(
            (payment, i) =>
                ({
                    id: i.toString(),
                    amount: payment.amount,
                    createdUtc: payment.dueDateUtc,
                    state: PaymentTableRecordPaymentStatus.Canceled,
                    paymentMethodData: data.paymentMethodData,
                    isCancelledPayment: true,
                }) as PaymentPlanDetailOverviewTableRecord
        ) ?? [];

    const historicalPayments =
        data?.historicalPayments.map(
            (payment: HistoricalPaymentTableRecord) =>
                ({
                    ...payment,
                    state: payment.paymentStatus,
                    actions: payment.paymentActions,
                }) as PaymentPlanDetailOverviewTableRecord
        ) ?? [];

    const tableData = [
        ...(data && showNextScheduledPayment(data)
            ? [
                  {
                      id: data.nextFuturePaymentId,
                      amount: data.nextPaymentAmount,
                      createdUtc: data.nextPaymentDateUtc,
                      paymentMethodData: data.paymentMethodData,
                      isFuturePayment: true,
                  } as PaymentPlanDetailOverviewTableRecord,
              ]
            : []),
        ...[...cancelledPayments, ...historicalPayments].sort(comparePaymentPlanDetailOverviewRecordsDesc),
    ];
    const columns = useMemo(
        () => getPaymentPlanDetailsOverviewTableMapping({ accountId, paymentPlanId }),
        [accountId, paymentPlanId]
    );

    return (
        <Stack vertical>
            <TableHeader
                title={lt('PaymentPlanDetailsOverviewTableTitle')}
                rightSide={
                    <PaymentPlanDetailsOverviewTableHeaderActions
                        paymentPlanId={paymentPlanId}
                        reservationId={data?.reservationId}
                        accountId={accountId}
                        paymentMethodData={data?.paymentMethodData}
                    />
                }
            />
            <OptimusProvider density="spacious">
                <StandaloneTable
                    columns={columns}
                    data={tableData}
                    uniqueRowId="id"
                    state={tableState}
                    onStateChange={setTableState}
                    amountOfRows={tableData.length}
                    loading={isFetching}
                    emptyState={{
                        primaryText: lt('PaymentPlanDetailsOverviewTableEmptyStatePrimaryText'),
                    }}
                />
            </OptimusProvider>
        </Stack>
    );
};
